import { FormEvent, useState } from "react";
import { toast } from "sonner";

import { formatDateTime } from "@buildpy/core/lib/format";
import {
  QNA_BODY_MAX,
  QNA_REASON_MAX,
  QNA_REPORT_CATEGORIES,
  QNA_REPORT_CATEGORY_LABEL,
  QNA_TOPIC_LABEL,
  type QnaPostKind,
  type QnaReportCategory,
} from "@buildpy/core/modules/process-standard/qna/types";
import { PRICE_INFO_QNA_NOTICE } from "@buildpy/core/modules/process-standard/types";
import type { QnaAnswerItem, QnaQuestionPageView } from "@buildpy/core/views/g5a/qna";
import { Button } from "@buildpy/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@buildpy/ui/dialog";
import EmptyState from "@buildpy/ui/empty-state";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { SimpleSelect } from "@buildpy/ui/select";
import { Textarea } from "@buildpy/ui/textarea";

import { answerQuestion, reportPost } from "@/api/actions/process-standard";
import Field from "@/components/field";
import { Link } from "@/lib/nav";

import { DELETED_TEXT } from "./qna-feed";
import { tabHref, topicHref } from "./standards-search";
import { useStandardAction } from "./use-standard-action";

const CATEGORY_OPTIONS = QNA_REPORT_CATEGORIES.map((c) => ({ value: c, label: QNA_REPORT_CATEGORY_LABEL[c] }));

// 신고할 수 있나 — 남의 글 · 안 지워진 글(같은 회사 중복 등 최종 판정은 RPC).
export const canReport = (post: { is_mine: boolean; deleted: boolean }): boolean => !post.is_mine && !post.deleted;

// 신고 — 가격·단가·원가 언급이나 기타 · 사유는 선택.
export const ReportDialog = ({
  kind,
  postId,
  open,
  onOpenChange,
}: {
  kind: QnaPostKind;
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [category, setCategory] = useState<QnaReportCategory>("PRICE");
  const [reason, setReason] = useState("");
  const { pending, run } = useStandardAction();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = reason.trim();
    run(
      () => reportPost({ post_kind: kind, post_id: postId, category, ...(trimmed ? { reason: trimmed } : {}) }),
      () => {
        onOpenChange(false);
        setReason("");
        toast.success("신고했습니다");
      }
    );
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{kind === "question" ? "질문 신고" : "답변 신고"}</DialogTitle>
          <DialogDescription>가격·단가·원가 이야기나 부적절한 글을 운영자에게 알립니다.</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-y-3" data-testid="report-form" onSubmit={onSubmit}>
          <fieldset className="flex flex-col gap-y-3" disabled={pending}>
            <Field label="종류">
              <SimpleSelect onValueChange={(v) => setCategory(v as QnaReportCategory)} options={CATEGORY_OPTIONS} value={category} />
            </Field>
            <Field label="사유 (선택)">
              <Textarea className="min-h-20 text-sm" maxLength={QNA_REASON_MAX} onChange={(e) => setReason(e.target.value)} value={reason} />
            </Field>
          </fieldset>
          <DialogFooter>
            <Button disabled={pending} onClick={() => onOpenChange(false)} size="sm" type="button" variant="ghost">
              취소
            </Button>
            <Button disabled={pending} size="sm" type="submit">
              신고
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// 글 하나의 머리 — 작성자 「시공사 · 권역」·시각·내 글·신고 버튼.
const PostMeta = ({ kind, post }: { kind: QnaPostKind; post: { id: string; author: string; is_mine: boolean; deleted: boolean; created_at: string } }) => {
  const [reporting, setReporting] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span>{post.author}</span>
      <span>{formatDateTime(post.created_at)}</span>
      {post.is_mine && <Pill tone="primary">내 글</Pill>}
      {canReport(post) && (
        <>
          <Button className="ml-auto" onClick={() => setReporting(true)} size="compact" variant="ghost-muted">
            신고
          </Button>
          <ReportDialog kind={kind} onOpenChange={setReporting} open={reporting} postId={post.id} />
        </>
      )}
    </div>
  );
};

const AnswerItem = ({ answer }: { answer: QnaAnswerItem }) => (
  <li className="flex flex-col gap-y-1 py-3 first:pt-0 last:pb-0" data-testid="qna-answer">
    <PostMeta kind="answer" post={answer} />
    <p className={answer.deleted ? "text-sm text-muted-foreground" : "whitespace-pre-line break-words text-sm"}>
      {answer.deleted ? DELETED_TEXT : answer.body}
    </p>
  </li>
);

// 답변 쓰기 — 저장하면 같은 화면이 다시 읽힌다.
export const AnswerForm = ({ questionId }: { questionId: string }) => {
  const [body, setBody] = useState("");
  const { pending, run } = useStandardAction();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(
      () => answerQuestion({ question_id: questionId, body }),
      () => {
        setBody("");
        toast.success("답변을 올렸습니다");
      }
    );
  };

  return (
    <form className="flex flex-col gap-y-3" data-testid="answer-form" onSubmit={onSubmit}>
      <Field label="답변">
        <Textarea className="min-h-24 text-sm" disabled={pending} maxLength={QNA_BODY_MAX} onChange={(e) => setBody(e.target.value)} value={body} />
      </Field>
      <p className="text-xs text-muted-foreground">{PRICE_INFO_QNA_NOTICE}</p>
      <div className="flex justify-end">
        <Button disabled={pending || body.trim() === ""} size="sm" type="submit">
          답변 올리기
        </Button>
      </div>
    </form>
  );
};

// 질문 하나와 답변 — 지워진 글은 「삭제된 글」 로 자리만 남는다.
const QnaQuestion = ({ data }: { data: QnaQuestionPageView }) => {
  const question = data.question;
  if (!question) {
    return (
      <div className="flex flex-col gap-y-3">
        <Link className="text-sm text-muted-foreground underline-offset-4 hover:underline" href={tabHref("qna")}>
          ← Q&A 목록
        </Link>
        <EmptyState>질문을 찾을 수 없습니다.</EmptyState>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-y-4">
      <Link className="text-sm text-muted-foreground underline-offset-4 hover:underline" href={topicHref(question.topic)}>
        ← {QNA_TOPIC_LABEL[question.topic]} 목록
      </Link>
      <SectionCard data-testid="qna-question">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Pill>{QNA_TOPIC_LABEL[question.topic]}</Pill>
          <h2 className={question.deleted ? "text-base font-semibold text-muted-foreground" : "text-base font-semibold"}>
            {question.deleted ? DELETED_TEXT : question.title}
          </h2>
        </div>
        <PostMeta kind="question" post={question} />
        {!question.deleted && <p className="mt-3 whitespace-pre-line break-words text-sm">{question.body}</p>}
      </SectionCard>
      <SectionCard title={`답변 ${data.answers.length}`}>
        {data.answers.length === 0 ? (
          <EmptyState variant="plain">아직 답변이 없습니다.</EmptyState>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {data.answers.map((answer) => (
              <AnswerItem key={answer.id} answer={answer} />
            ))}
          </ul>
        )}
        {!question.deleted && (
          <div className="mt-4 border-t border-border pt-4">
            <AnswerForm questionId={question.id} />
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default QnaQuestion;
